// Entity detail page for LEGO items (sets, parts, minifigs, elements)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { authService } from '../services/authService';
import { trackLinkClick } from '../services/linkTracking';
import { parseMarkdownLink, getPlatformFromUrl } from '../utils/linkUtils';
import { toSingular } from '../types/entities';
import {
  type EntityDetailsResponse,
  type EntityData,
  type SuggestionLink,
  isSetData,
  isPartData,
  isMinifigData,
  isElementData,
} from '../types/entityData';
import EntityHover from '../components/EntityHover';
import ImageWithPlaceholder from '../components/ImageWithPlaceholder';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { ROUTES } from '../constants/routes';
import '../styles/EntityDetailPage.css';
import '../styles/ImagePlaceholder.css';

type EntityType = 'sets' | 'parts' | 'minifigs' | 'elements';

const EntityDetailPage: React.FC = () => {
  const { type, id } = useParams<{ type: EntityType; id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [structuredData, setStructuredData] = useState<EntityData | null>(null);
  const [structuredSuggestions, setStructuredSuggestions] = useState<SuggestionLink[]>([]);
  // Keep markdown as fallback for backward compatibility
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sourceConversationId = searchParams.get('conversationId');

  useEffect(() => {
    const fetchEntityDetails = async () => {
      if (!type || !id) {
        setError('Invalid entity type or ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.append('includeContent', 'true');
        params.append('context', 'detail');
        const colorId = searchParams.get('color');
        if (colorId) {
          params.append('colorId', colorId);
        }
        const apiUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/entities/${type}/${id}?${params.toString()}`;

        const token = authService.getToken();
        const headers: HeadersInit = {
          'Accept': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(apiUrl, { headers });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            setError('Please log in to view entity details');
          } else {
            const errorText = await response.text();
            console.error(`API error ${response.status}:`, errorText);
            setError(`Failed to fetch entity data: ${response.status}`);
          }
          setLoading(false);
          return;
        }

        const data: EntityDetailsResponse = await response.json();

        // Prefer structured data (#435)
        if (data.structuredData) {
          setStructuredData(data.structuredData);
        }
        if (data.structuredSuggestions) {
          setStructuredSuggestions(data.structuredSuggestions);
        }

        // Keep legacy fields as fallback
        setMarkdown(data.content);
        setSuggestions(data.suggestions);
      } catch (err) {
        setError('Failed to load entity details');
        console.error('Error fetching entity details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEntityDetails();
  }, [type, id, searchParams]);

  // Custom markdown components to support entity hover links and image placeholders
  const markdownComponents = {
    img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => (
      <ImageWithPlaceholder src={src} alt={alt} />
    ),
    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      let linkPath = href;
      if (href) {
        try {
          const url = new URL(href, window.location.origin);
          if (url.origin === window.location.origin ||
              url.hostname === 'brick.directory' ||
              url.hostname.endsWith('.brick.directory')) {
            linkPath = url.pathname + url.search;
          }
        } catch {
          // Not a valid URL, use as-is
        }
      }

      if (linkPath?.startsWith('/items/')) {
        const match = linkPath.match(/^\/items\/(sets|parts|minifigs|elements)\/([^?]+)(\?.*)?$/);
        if (match) {
          const [, entityType, entityId, queryString] = match;
          const params = new URLSearchParams(queryString || '');

          return (
            <EntityHover
              type={entityType as EntityType}
              id={entityId}
              colorId={params.get('color') || undefined}
              className="entity-detail-link"
              onSuggestionClick={(suggestion) => {
                const targetRoute = sourceConversationId
                  ? ROUTES.getConversationRoute(sourceConversationId)
                  : ROUTES.NEW_CONVERSATION;
                navigate(`${targetRoute}?q=${encodeURIComponent(suggestion)}`);
              }}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.button === 1) return;
                e.preventDefault();
                navigate(linkPath);
              }}
            >
              {children}
            </EntityHover>
          );
        }
      }

      return (
        <a href={href} {...props} target="_blank" rel="noopener noreferrer" className="entity-detail-link">
          {children}
        </a>
      );
    }
  };

  const getEntityTypeName = (type?: EntityType): string => {
    switch (type) {
      case 'sets': return 'Set';
      case 'parts': return 'Part';
      case 'minifigs': return 'Minifigure';
      case 'elements': return 'Element';
      default: return 'Item';
    }
  };

  const handleExternalLinkClick = (url: string) => {
    if (!type || !id) return;
    const platform = getPlatformFromUrl(url);
    if (platform) {
      trackLinkClick({
        entityType: toSingular(type),
        entityId: id,
        platform,
        url,
        referrer: 'detail-page',
      });
    }
  };

  const handleSuggestionClick = (text: string) => {
    const targetRoute = sourceConversationId
      ? ROUTES.getConversationRoute(sourceConversationId)
      : ROUTES.NEW_CONVERSATION;
    navigate(`${targetRoute}?q=${encodeURIComponent(text)}`);
  };

  if (loading) {
    return (
      <div className="entity-detail-page">
        <div className="entity-detail-container">
          <div className="entity-detail-loading">
            <LoadingAnimation
              size={120}
              message={`Loading ${getEntityTypeName(type)} details...`}
            />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="entity-detail-page">
        <div className="entity-detail-container">
          <div className="entity-detail-error">
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={() => navigate(ROUTES.HOME)} className="back-button">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render suggestions using structured suggestions when available, falling back to legacy.
   */
  const renderSuggestions = () => {
    const suggestionsToRender = structuredSuggestions.length > 0 ? structuredSuggestions : null;

    if (suggestionsToRender && suggestionsToRender.length > 0) {
      return (
        <div className="suggestions-list">
          {suggestionsToRender.map((suggestion, index) => {
            if (suggestion.type === 'external' && suggestion.url) {
              return (
                <a
                  key={index}
                  href={suggestion.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="suggestion-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExternalLinkClick(suggestion.url!);
                  }}
                >
                  {suggestion.emoji ? `${suggestion.emoji} ` : ''}{suggestion.text}
                </a>
              );
            }

            return (
              <button
                key={index}
                className="suggestion-button"
                onClick={() => handleSuggestionClick(suggestion.text)}
              >
                {suggestion.text}
              </button>
            );
          })}
        </div>
      );
    }

    // Legacy fallback using parseMarkdownLink
    if (suggestions.length > 0) {
      return (
        <div className="suggestions-list">
          {suggestions.map((suggestion, index) => {
            const link = parseMarkdownLink(suggestion);
            if (link) {
              return (
                <a key={index} href={link.url} target="_blank" rel="noopener noreferrer"
                  className="suggestion-link"
                  onClick={(e) => { e.stopPropagation(); handleExternalLinkClick(link.url); }}>
                  {link.text}
                </a>
              );
            }
            return (
              <button key={index} className="suggestion-button"
                onClick={() => handleSuggestionClick(suggestion)}>
                {suggestion}
              </button>
            );
          })}
        </div>
      );
    }

    return null;
  };

  /**
   * Render the structured data detail page content.
   */
  const renderStructuredDetail = () => {
    if (!structuredData) return null;

    const title = 'name' in structuredData ? structuredData.name : id;
    const imageUrl = 'imageUrl' in structuredData ? (structuredData.imageUrl as string | undefined) : undefined;

    return (
      <>
        <div className="entity-header">
          <button onClick={() => navigate(-1)} className="back-button" title="Go back">
            <svg className="back-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21.99 3L2 12l19.99 9-.01-7-15-2 15-2z" />
            </svg>
          </button>
          {title && <h1 className="entity-title">{title}</h1>}
        </div>

        <div className="entity-detail-content">
          {imageUrl && <ImageWithPlaceholder src={imageUrl} alt={title || ''} />}

          {/* Set-specific overview */}
          {isSetData(structuredData) && (
            <table className="entity-overview-table">
              <tbody>
                <tr><td><strong>Set Number</strong></td><td>{structuredData.setNum}</td></tr>
                {structuredData.year && <tr><td><strong>Year</strong></td><td>{structuredData.year}</td></tr>}
                {structuredData.theme && <tr><td><strong>Theme</strong></td><td>{structuredData.theme}</td></tr>}
                <tr><td><strong>Total Parts</strong></td><td>{structuredData.totalParts.toLocaleString()}</td></tr>
                <tr><td><strong>Minifigures</strong></td><td>{structuredData.totalMinifigures}</td></tr>
                {structuredData.totalSubSets > 0 && <tr><td><strong>Sub-sets</strong></td><td>{structuredData.totalSubSets}</td></tr>}
                {structuredData.bricklinkId && <tr><td><strong>BrickLink ID</strong></td><td>{structuredData.bricklinkId}</td></tr>}
                {structuredData.bricklinkName && <tr><td><strong>BrickLink Name</strong></td><td>{structuredData.bricklinkName}</td></tr>}
              </tbody>
            </table>
          )}

          {/* Part-specific overview */}
          {isPartData(structuredData) && (
            <table className="entity-overview-table">
              <tbody>
                <tr><td><strong>Part Number</strong></td><td>{structuredData.partNum}</td></tr>
                {structuredData.category && <tr><td><strong>Category</strong></td><td>{structuredData.category}</td></tr>}
                {structuredData.material && <tr><td><strong>Material</strong></td><td>{structuredData.material}</td></tr>}
                {structuredData.totalUsages != null && <tr><td><strong>Used in Sets</strong></td><td>{structuredData.totalUsages}</td></tr>}
                {structuredData.uniqueColors != null && <tr><td><strong>Available Colors</strong></td><td>{structuredData.uniqueColors}</td></tr>}
                {structuredData.totalQuantity != null && <tr><td><strong>Total Quantity</strong></td><td>{structuredData.totalQuantity.toLocaleString()}</td></tr>}
                {structuredData.bricklinkId && <tr><td><strong>BrickLink ID</strong></td><td>{structuredData.bricklinkId}</td></tr>}
              </tbody>
            </table>
          )}

          {/* Minifig-specific overview */}
          {isMinifigData(structuredData) && (
            <table className="entity-overview-table">
              <tbody>
                <tr><td><strong>Figure Number</strong></td><td>{structuredData.figNum}</td></tr>
                {structuredData.numParts != null && <tr><td><strong>Parts</strong></td><td>{structuredData.numParts}</td></tr>}
                {structuredData.bricklinkId && <tr><td><strong>BrickLink ID</strong></td><td>{structuredData.bricklinkId}</td></tr>}
              </tbody>
            </table>
          )}

          {/* Element-specific overview */}
          {isElementData(structuredData) && (
            <table className="entity-overview-table">
              <tbody>
                <tr><td><strong>Element ID</strong></td><td>{structuredData.elementId}</td></tr>
                <tr><td><strong>Part Number</strong></td><td>{structuredData.partNum}</td></tr>
                <tr><td><strong>Part Name</strong></td><td>{structuredData.partName}</td></tr>
                <tr><td><strong>Color</strong></td><td>{structuredData.colorName}</td></tr>
                {structuredData.colorHex && <tr><td><strong>Color Code</strong></td><td>{structuredData.colorHex}</td></tr>}
                {structuredData.isTransparent != null && <tr><td><strong>Transparent</strong></td><td>{structuredData.isTransparent ? 'Yes' : 'No'}</td></tr>}
                {structuredData.category && <tr><td><strong>Category</strong></td><td>{structuredData.category}</td></tr>}
                {structuredData.material && <tr><td><strong>Material</strong></td><td>{structuredData.material}</td></tr>}
              </tbody>
            </table>
          )}
        </div>

        {renderSuggestions()}

        {/* Render markdown detail sections (parts tables, minifig tables, etc.) */}
        {markdown && (
          <div className="entity-detail-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {markdown}
            </ReactMarkdown>
          </div>
        )}
      </>
    );
  };

  /**
   * Legacy rendering path using markdown only (fallback when no structured data).
   */
  const renderLegacyMarkdown = () => {
    if (!markdown) return null;

    // Extract title and split markdown
    const titleRegex = /^#\s+(.+?)$/m;
    const titleMatch = markdown.match(titleRegex);
    const title = titleMatch ? titleMatch[1] : '';
    const contentWithoutTitle = titleMatch ? markdown.replace(titleRegex, '').trim() : markdown;

    let beforeImage = contentWithoutTitle;
    let afterImage = '';

    if (type !== 'parts') {
      const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/;
      const imageMatch = contentWithoutTitle.match(imageRegex);
      if (imageMatch) {
        const imageIndex = imageMatch.index! + imageMatch[0].length;
        beforeImage = contentWithoutTitle.substring(0, imageIndex);
        afterImage = contentWithoutTitle.substring(imageIndex);
      }
    }

    return (
      <>
        <div className="entity-header">
          <button onClick={() => navigate(-1)} className="back-button" title="Go back">
            <svg className="back-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21.99 3L2 12l19.99 9-.01-7-15-2 15-2z" />
            </svg>
          </button>
          {title && <h1 className="entity-title">{title}</h1>}
        </div>

        <div className="entity-detail-content">
          {beforeImage && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {beforeImage}
            </ReactMarkdown>
          )}
        </div>

        {renderSuggestions()}

        <div className="entity-detail-content">
          {afterImage && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {afterImage}
            </ReactMarkdown>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="entity-detail-page">
      <div className="entity-detail-container">
        {structuredData ? renderStructuredDetail() : renderLegacyMarkdown()}
      </div>
    </div>
  );
};

export default EntityDetailPage;
